import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: January 28, 2026</p>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Zentry ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our GRC platform and related services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Account Information:</strong> Name, email address, password, organization name</li>
              <li><strong>Profile Information:</strong> Job title, phone number, profile picture</li>
              <li><strong>Compliance Data:</strong> Policies, controls, evidence, risk assessments</li>
              <li><strong>Payment Information:</strong> Billing address, payment method (processed by Stripe)</li>
              <li><strong>Communications:</strong> Support tickets, feedback, survey responses</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Usage Data:</strong> Pages visited, features used, actions taken</li>
              <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
              <li><strong>Cookies:</strong> Session cookies for authentication, preference cookies</li>
              <li><strong>Log Data:</strong> Access times, error logs, referral URLs</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">2.3 Information from Integrations</h3>
            <p className="text-gray-700 leading-relaxed">
              When you connect third-party services (AWS, GitHub, Okta, etc.), we collect data necessary to provide automated evidence collection and compliance monitoring.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We use your information to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Provide, maintain, and improve the Service</li>
              <li>Process transactions and send billing notifications</li>
              <li>Send compliance alerts, digests, and product updates</li>
              <li>Respond to support requests and communications</li>
              <li>Analyze usage patterns to improve user experience</li>
              <li>Detect and prevent fraud, abuse, and security threats</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We do NOT sell your personal information. We may share data with:</p>
            
            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">4.1 Service Providers</h3>
            <p className="text-gray-700 leading-relaxed">
              Third-party vendors who assist in providing the Service (e.g., cloud hosting, payment processing, email delivery). These providers are bound by confidentiality agreements.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">4.2 Within Your Organization</h3>
            <p className="text-gray-700 leading-relaxed">
              Data is shared with other users in your organization as permitted by your account settings and user roles.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">4.3 Legal Requirements</h3>
            <p className="text-gray-700 leading-relaxed">
              We may disclose information if required by law, subpoena, or government request, or to protect our rights and safety.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">4.4 Business Transfers</h3>
            <p className="text-gray-700 leading-relaxed">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We implement robust security measures including:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Encryption:</strong> AES-256 encryption at rest, TLS 1.3 in transit</li>
              <li><strong>Access Controls:</strong> Role-based access control (RBAC)</li>
              <li><strong>Authentication:</strong> Secure password hashing, optional MFA</li>
              <li><strong>Monitoring:</strong> 24/7 security monitoring and intrusion detection</li>
              <li><strong>Auditing:</strong> Complete audit logs of all system access</li>
              <li><strong>Compliance:</strong> SOC 2 Type II certified infrastructure</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain your data for as long as your account is active or as needed to provide services. After account termination:
            </p>
            <ul className="list-disc pl-6 mt-4 text-gray-700 space-y-2">
              <li>Active data is deleted within 30 days upon request</li>
              <li>Backups are purged within 90 days</li>
              <li>Anonymized analytics data may be retained indefinitely</li>
              <li>Legal/compliance records are retained as required by law</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Portability:</strong> Receive your data in a portable format</li>
              <li><strong>Objection:</strong> Object to certain processing activities</li>
              <li><strong>Restriction:</strong> Request limited processing of your data</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise these rights, contact us at <strong>privacy@zentry.io</strong>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place, including Standard Contractual Clauses for EU data transfers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Cookies and Tracking</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We use cookies for:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for authentication and security</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              <li><strong>Analytics Cookies:</strong> Understand how you use the Service</li>
            </ul>
            <p className="text-gray-700 mt-4">
              You can control cookies through your browser settings. Disabling essential cookies may affect Service functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              The Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. GDPR Compliance (EU Users)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">For EU residents, we process data under the following legal bases:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Contract:</strong> Processing necessary to provide the Service</li>
              <li><strong>Legitimate Interest:</strong> Improving and securing the Service</li>
              <li><strong>Consent:</strong> Marketing communications (opt-in)</li>
              <li><strong>Legal Obligation:</strong> Compliance with applicable laws</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. CCPA Compliance (California Users)</h2>
            <p className="text-gray-700 leading-relaxed">
              California residents have additional rights under CCPA, including the right to know what data we collect, the right to delete, and the right to opt-out of data sales. We do not sell personal information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy periodically. We will notify you of material changes via email or in-app notification. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have questions about this Privacy Policy or our data practices, contact us at:
            </p>
            <p className="text-gray-700 mt-4">
              <strong>Privacy Inquiries:</strong> privacy@zentry.io<br />
              <strong>General Support:</strong> support@zentry.io<br />
              <strong>Data Protection Officer:</strong> dpo@zentry.io
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-gray-500 text-sm">
          © 2026 Zentry. All rights reserved. | 
          <Link to="/terms" className="ml-2 hover:text-gray-700">Terms and Conditions</Link>
        </div>
      </footer>
    </div>
  );
}
